import { useEffect, useState } from "react";
import {
  getSettings,
  saveSettings,
  getConfig,
  listFeatureFlags,
  updateFeatureFlag,
  listCrmConnections,
  connectCrm,
  disconnectCrm,
} from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, AlertTriangle, Mail, Plug, Link2, Unlink } from "lucide-react";

export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी · Hindi" },
  { code: "ar", label: "العربية · Arabic" },
  { code: "es", label: "Español · Spanish" },
  { code: "fr", label: "Français · French" },
  { code: "de", label: "Deutsch · German" },
];

const EXAMPLE = {
  product_name: "Track My Employees",
  product_description:
    "Tracks real-time team productivity, work patterns, and time usage without micromanagement.",
  pain_point: "Lack of visibility into productive hours in remote teams",
  target_icp:
    "Founders / CTOs / Ops Heads at remote-first SaaS / IT teams (10–200 employees)",
  problems_solved:
    "Managers don't know who is actually productive vs just busy\nRemote teams lose 20–30% efficiency due to lack of visibility\nFounders rely on activity (Slack, Jira) instead of real output",
  differentiation:
    "Tracks actual work behavior, not just tasks\nNo micromanagement feeling (non-invasive)\nSetup in minutes (not weeks like enterprise tools)",
  proof_points:
    "Improves productivity visibility within days\nReduces hidden inefficiencies by ~20–30%",
  industry: "SaaS\nIT services\nAI / ML\nSoftware product companies",
  company_size: "10–200 employees",
  decision_maker_roles: "Founder\nCEO\nCTO\nVP Engineering\nHead of Operations",
  pain_signals: "Remote team\nProductivity issues\nScaling fast",
  triggers: "Hiring remotely\nRecently funded\nGrowing team",
  disqualifiers: "E-commerce\nOffline business\n<5 employees\nRetail / fashion",
  preferred_language: "en",
  calendar_link: "https://calendly.com/satyarthgaur/15min",
  sender_name: "Satyarth Gaur",
  sender_email: "",
  reply_to_email: "satyarthgaur@gmail.com",
};

// Helpers: textarea (newline-separated) <-> array
const linesToArray = (s) =>
  (s || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

export default function Setup() {
  const [form, setForm] = useState({
    product_name: "",
    product_description: "",
    pain_point: "",
    target_icp: "",
    problems_solved: "",
    differentiation: "",
    proof_points: "",
    industry: "",
    company_size: "",
    decision_maker_roles: "",
    pain_signals: "",
    triggers: "",
    disqualifiers: "",
    preferred_language: "en",
    calendar_link: "",
    sender_name: "",
    sender_email: "",
    reply_to_email: "",
    execution_mode: "assisted",
  });
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([getSettings(), getConfig().catch(() => null)])
      .then(([s, c]) => {
        if (s && s.product_name) {
          setForm({
            product_name: s.product_name || "",
            product_description: s.product_description || "",
            pain_point: s.pain_point || "",
            target_icp: s.target_icp || "",
            problems_solved: arrayToLines(s.problems_solved),
            differentiation: arrayToLines(s.differentiation),
            proof_points: arrayToLines(s.proof_points),
            industry: arrayToLines(s.industry),
            company_size: s.company_size || "",
            decision_maker_roles: arrayToLines(s.decision_maker_roles),
            pain_signals: arrayToLines(s.pain_signals),
            triggers: arrayToLines(s.triggers),
            disqualifiers: arrayToLines(s.disqualifiers),
            preferred_language: s.preferred_language || "en",
            calendar_link: s.calendar_link || "",
            sender_name: s.sender_name || "",
            sender_email: s.sender_email || "",
            reply_to_email: s.reply_to_email || "",
            execution_mode: s.execution_mode || "assisted",
          });
        }
        setConfig(c);
      })
      .finally(() => setLoaded(true));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        problems_solved: linesToArray(form.problems_solved),
        differentiation: linesToArray(form.differentiation),
        proof_points: linesToArray(form.proof_points),
        industry: linesToArray(form.industry),
        decision_maker_roles: linesToArray(form.decision_maker_roles),
        pain_signals: linesToArray(form.pain_signals),
        triggers: linesToArray(form.triggers),
        disqualifiers: linesToArray(form.disqualifiers),
      };
      await saveSettings(payload);
      const c = await getConfig().catch(() => null);
      setConfig(c);
      toast.success("Settings saved. Sender and qualifier calibrated.");
    } catch (err) {
      toast.error("Failed to save", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const senderUnverified = config && config.sender_verified === false;

  return (
    <div
      className="px-6 md:px-10 py-8 max-w-3xl mx-auto"
      data-testid="setup-page"
    >
      <div className="label-caps text-[#002FA7]">Step 1</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
        Describe what you sell.
      </h1>
      <p className="mt-2 text-[15px] text-slate-600">
        The AI uses this to score leads, pick hooks, and write emails that don't
        sound like every other SaaS pitch.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-8 rounded-md border border-slate-200 bg-white p-8 space-y-6"
      >
        <Field label="Product name" htmlFor="product_name">
          <Input
            id="product_name"
            data-testid="input-product-name"
            value={form.product_name}
            onChange={onChange("product_name")}
            placeholder="Track My Employees"
            className="h-11"
            required
          />
        </Field>

        <Field label="Product description" htmlFor="product_description">
          <Textarea
            id="product_description"
            data-testid="input-product-description"
            value={form.product_description}
            onChange={onChange("product_description")}
            placeholder="What does your product do in one line?"
            className="min-h-[84px]"
            required
          />
        </Field>

        <Field label="Target problem (pain point)" htmlFor="pain_point">
          <Textarea
            id="pain_point"
            data-testid="input-pain-point"
            value={form.pain_point}
            onChange={onChange("pain_point")}
            placeholder="Lack of visibility in remote teams"
            className="min-h-[64px]"
            required
          />
        </Field>

        <Field label="Target ICP" htmlFor="target_icp">
          <Input
            id="target_icp"
            data-testid="input-target-icp"
            value={form.target_icp}
            onChange={onChange("target_icp")}
            placeholder="IT companies with 20–200 employees"
            className="h-11"
            required
          />
        </Field>

        {/* ICP TARGETING — structured scoring inputs */}
        <div className="pt-2">
          <div className="label-caps !text-[10px] !tracking-[0.2em] text-[#002FA7] mb-1">
            ICP targeting (the brain of the SDR)
          </div>
          <p className="text-[12px] text-slate-500 mb-4 leading-relaxed">
            One item per line. Disqualifiers hard-reject. Pain signals & triggers
            boost score and shape the email voice.
          </p>

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Target industries" htmlFor="industry">
                <Textarea
                  id="industry"
                  data-testid="input-industry"
                  value={form.industry}
                  onChange={onChange("industry")}
                  placeholder={"SaaS\nIT services\nAI / ML"}
                  className="min-h-[76px] font-mono text-[12.5px]"
                />
              </Field>
              <Field label="Company size" htmlFor="company_size">
                <Input
                  id="company_size"
                  data-testid="input-company-size"
                  value={form.company_size}
                  onChange={onChange("company_size")}
                  placeholder="10–200 employees"
                  className="h-11"
                />
              </Field>
            </div>

            <Field label="Decision-maker roles" htmlFor="decision_maker_roles">
              <Textarea
                id="decision_maker_roles"
                data-testid="input-decision-maker-roles"
                value={form.decision_maker_roles}
                onChange={onChange("decision_maker_roles")}
                placeholder={"Founder\nCEO\nCTO\nVP Engineering\nHead of Operations"}
                className="min-h-[88px] font-mono text-[12.5px]"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Pain signals" htmlFor="pain_signals">
                <Textarea
                  id="pain_signals"
                  data-testid="input-pain-signals"
                  value={form.pain_signals}
                  onChange={onChange("pain_signals")}
                  placeholder={"Remote team\nProductivity issues\nScaling fast"}
                  className="min-h-[76px] font-mono text-[12.5px]"
                />
              </Field>
              <Field label="Triggers / buying intent" htmlFor="triggers">
                <Textarea
                  id="triggers"
                  data-testid="input-triggers"
                  value={form.triggers}
                  onChange={onChange("triggers")}
                  placeholder={"Hiring remotely\nRecently funded\nGrowing team"}
                  className="min-h-[76px] font-mono text-[12.5px]"
                />
              </Field>
            </div>

            <Field label="Hard disqualifiers (auto-reject)" htmlFor="disqualifiers">
              <Textarea
                id="disqualifiers"
                data-testid="input-disqualifiers"
                value={form.disqualifiers}
                onChange={onChange("disqualifiers")}
                placeholder={"E-commerce\nOffline business\n<5 employees"}
                className="min-h-[76px] font-mono text-[12.5px] border-red-200 focus-visible:ring-red-400"
              />
              <p className="text-[11.5px] text-red-700 mt-1.5 leading-relaxed">
                Any match here forces REJECT, no exceptions.
              </p>
            </Field>

            <Field label="Preferred outreach language" htmlFor="preferred_language">
              <Select
                value={form.preferred_language}
                onValueChange={(v) =>
                  setForm({ ...form, preferred_language: v })
                }
              >
                <SelectTrigger
                  id="preferred_language"
                  data-testid="select-preferred-language"
                  className="h-11"
                >
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((l) => (
                    <SelectItem
                      key={l.code}
                      value={l.code}
                      data-testid={`lang-option-${l.code}`}
                    >
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
                Emails drafted in English, then adapted (not translated) to this
                language. Falls back to English if confidence is low.
              </p>
            </Field>

            <Field label="Execution mode" htmlFor="execution_mode">
              <Select
                value={form.execution_mode}
                onValueChange={(v) => {
                  if (v === "autonomous" && form.execution_mode !== "autonomous") {
                    const ok = window.confirm(
                      "Autonomous mode will allow the system to send emails and make outbound calls AUTOMATICALLY without your approval.\n\n" +
                        "Are you sure you want to enable it?",
                    );
                    if (!ok) return;
                  }
                  setForm({ ...form, execution_mode: v });
                }}
              >
                <SelectTrigger
                  id="execution_mode"
                  data-testid="select-execution-mode"
                  className="h-11"
                >
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual" data-testid="mode-option-manual">
                    Manual — no automation
                  </SelectItem>
                  <SelectItem value="assisted" data-testid="mode-option-assisted">
                    Assisted — suggest & approve (recommended)
                  </SelectItem>
                  <SelectItem
                    value="autonomous"
                    data-testid="mode-option-autonomous"
                  >
                    Autonomous — full auto-execution
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
                Controls how much the system does on its own.
                <strong className="text-slate-700"> Assisted</strong> schedules
                actions for you to approve.
                <strong className="text-slate-700"> Autonomous</strong> executes
                them automatically on a 30-second loop.
              </p>
            </Field>
          </div>
        </div>

        {/* POSITIONING CONTEXT — deepens email generation */}
        <div className="pt-2">
          <div className="label-caps !text-[10px] !tracking-[0.2em] text-[#002FA7] mb-1">
            Positioning context (feeds the AI)
          </div>
          <p className="text-[12px] text-slate-500 mb-4 leading-relaxed">
            One item per line. The AI picks the most relevant item per lead —
            never lists them as features.
          </p>

          <div className="space-y-5">
            <Field label="Top 3 problems this product solves" htmlFor="problems_solved">
              <Textarea
                id="problems_solved"
                data-testid="input-problems-solved"
                value={form.problems_solved}
                onChange={onChange("problems_solved")}
                placeholder={"Managers don't know who is actually productive vs just busy\nRemote teams lose 20–30% efficiency due to lack of visibility\nFounders rely on activity (Slack, Jira) instead of real output"}
                className="min-h-[88px] font-mono text-[12.5px]"
              />
            </Field>

            <Field label="Unique differentiation" htmlFor="differentiation">
              <Textarea
                id="differentiation"
                data-testid="input-differentiation"
                value={form.differentiation}
                onChange={onChange("differentiation")}
                placeholder={"Tracks actual work behavior, not just tasks\nNo micromanagement feeling (non-invasive)\nSetup in minutes (not weeks like enterprise tools)"}
                className="min-h-[76px] font-mono text-[12.5px]"
              />
            </Field>

            <Field label="Proof / credibility patterns (optional)" htmlFor="proof_points">
              <Textarea
                id="proof_points"
                data-testid="input-proof-points"
                value={form.proof_points}
                onChange={onChange("proof_points")}
                placeholder={"Improves productivity visibility within days\nReduces hidden inefficiencies by ~20–30%"}
                className="min-h-[64px] font-mono text-[12.5px]"
              />
            </Field>
          </div>
        </div>

        <Field label="Calendar link (optional, recommended)" htmlFor="calendar_link">
          <Input
            id="calendar_link"
            data-testid="input-calendar-link"
            value={form.calendar_link}
            onChange={onChange("calendar_link")}
            placeholder="https://calendly.com/yourname/15min"
            className="h-11 font-mono text-[13px]"
            type="url"
          />
          <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
            Injected naturally into meeting messages and smart replies when
            prospects show interest. Calendly, Cal.com, or any scheduling URL.
          </p>
        </Field>

        {/* EMAIL CONFIGURATION */}
        <div className="pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-4 w-4 text-[#002FA7]" />
            <div className="label-caps !text-[10.5px] !tracking-[0.2em] text-[#002FA7]">
              Email Configuration
            </div>
          </div>
          <p className="text-[12.5px] text-slate-500 mb-5 leading-relaxed">
            Send from your own domain. If the domain isn't verified in Resend,
            we'll fall back to the default sandbox sender and warn you.
          </p>

          {senderUnverified && (
            <div
              className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex gap-3"
              data-testid="sender-unverified-warning"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-amber-900 leading-relaxed">
                <div className="font-semibold">Domain not verified.</div>
                <div className="text-amber-800">
                  {config?.last_send_error ||
                    "Emails may not send or may fallback to default sender."}{" "}
                  Verify at{" "}
                  <a
                    href="https://resend.com/domains"
                    target="_blank"
                    rel="noreferrer"
                    className="underline font-medium"
                  >
                    resend.com/domains
                  </a>
                  .
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Sender name" htmlFor="sender_name">
              <Input
                id="sender_name"
                data-testid="input-sender-name"
                value={form.sender_name}
                onChange={onChange("sender_name")}
                placeholder="Satyarth Gaur"
                className="h-11"
              />
            </Field>
            <Field label="Sender email" htmlFor="sender_email">
              <Input
                id="sender_email"
                data-testid="input-sender-email"
                value={form.sender_email}
                onChange={onChange("sender_email")}
                placeholder="you@yourdomain.com"
                className="h-11 font-mono text-[13px]"
                type="email"
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Reply-to email (blank = same as sender)" htmlFor="reply_to_email">
              <Input
                id="reply_to_email"
                data-testid="input-reply-to-email"
                value={form.reply_to_email}
                onChange={onChange("reply_to_email")}
                placeholder="replies@yourdomain.com"
                className="h-11 font-mono text-[13px]"
                type="email"
              />
            </Field>
          </div>

          {config?.from_header && (
            <div
              className="mt-5 rounded-md bg-slate-50 border border-slate-200 px-4 py-3"
              data-testid="current-sender-display"
            >
              <div className="label-caps !text-[10px] text-slate-500">
                Current active sender
              </div>
              <div className="mt-1 font-mono text-[13px] text-slate-900">
                {config.from_header}
              </div>
              <div className="mt-0.5 text-[11.5px] text-slate-500">
                Reply-To:{" "}
                <span className="font-mono">{config.reply_to || "—"}</span>
                {config.sender_verified === false && (
                  <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-medium">
                    <AlertTriangle className="h-3 w-3" /> unverified — using fallback
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setForm({ ...form, ...EXAMPLE })}
            className="text-[12.5px] font-medium text-slate-600 hover:text-[#002FA7]"
            data-testid="load-example-btn"
          >
            ↺ Load example (Track My Employees)
          </button>
          <Button
            type="submit"
            disabled={saving || !loaded}
            data-testid="save-settings-btn"
            className="h-11 rounded-md bg-[#002FA7] hover:bg-[#002072] min-w-[180px]"
          >
            {saving ? (
              "Saving…"
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Save & calibrate
              </>
            )}
          </Button>
        </div>
      </form>

      {form.product_name && (
        <div className="mt-6 flex items-center gap-2 text-[13px] text-[#16A34A]">
          <CheckCircle2 className="h-4 w-4" />
          Calibrated for {form.product_name}.
        </div>
      )}

      {/* Feature Controls (Task 10 — Enterprise runtime flags) */}
      <FeatureControlsPanel />

      {/* CRM Integrations (Tasks 2-5 — HubSpot / Salesforce / Dynamics / Zoho) */}
      <CrmConnectionsPanel />
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="label-caps !text-[10px] !tracking-[0.2em] text-slate-500"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

// ============ FEATURE CONTROLS PANEL ============

function FeatureControlsPanel() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(null); // currently toggling key
  const [pendingConfirm, setPendingConfirm] = useState(null); // { key, newVal }

  useEffect(() => {
    listFeatureFlags().then((d) => setFlags(d.flags || [])).catch(() => setFlags([]));
  }, []);

  const applyToggle = async (key, newVal) => {
    setBusy(key);
    try {
      const r = await updateFeatureFlag(key, newVal);
      setFlags((curr) =>
        (curr || []).map((f) => (f.key === key ? { ...f, ...r.flag } : f))
      );
      toast.success(`${key} is now ${newVal ? "ON" : "OFF"}`);
    } catch (err) {
      toast.error("Flag update failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
      setPendingConfirm(null);
    }
  };

  const onToggle = (flag) => {
    const newVal = !flag.value;
    if (flag.critical && newVal === true) {
      setPendingConfirm({ key: flag.key, newVal, description: flag.description });
      return;
    }
    applyToggle(flag.key, newVal);
  };

  if (flags === null) {
    return (
      <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="label-caps text-[#002FA7]">Feature Controls</div>
        <div className="mt-3 space-y-2">
          <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-10 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="feature-controls-panel"
    >
      <div className="flex items-center gap-2">
        <div className="label-caps text-[#002FA7]">Feature Controls</div>
        <span className="text-[11px] text-slate-500">
          · {flags.length} runtime flags · changes audit-logged
        </span>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {flags.map((flag) => (
          <div
            key={flag.key}
            className="py-3 flex items-start justify-between gap-4"
            data-testid={`feature-flag-${flag.key}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-slate-900">
                  {flag.key}
                </span>
                {flag.critical && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                    Critical
                  </span>
                )}
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                    flag.value
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-slate-100 text-slate-600 border border-slate-200"
                  }`}
                  data-testid={`feature-flag-state-${flag.key}`}
                >
                  {flag.value ? "ON" : "OFF"}
                </span>
              </div>
              <div className="text-[12px] text-slate-600 mt-1 leading-snug">
                {flag.description}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onToggle(flag)}
              disabled={busy === flag.key}
              aria-label={`Toggle ${flag.key}`}
              className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                flag.value ? "bg-[#002FA7]" : "bg-slate-300"
              } ${busy === flag.key ? "opacity-60 cursor-wait" : ""}`}
              data-testid={`feature-flag-toggle-${flag.key}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  flag.value ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      {/* Confirmation dialog for critical ON-toggles */}
      {pendingConfirm && (
        <div
          className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4"
          data-testid="feature-flag-confirm"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-red-800">
                Turn on {pendingConfirm.key}?
              </div>
              <div className="text-[12px] text-red-700 mt-1">
                {pendingConfirm.description}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => applyToggle(pendingConfirm.key, pendingConfirm.newVal)}
                  disabled={busy === pendingConfirm.key}
                  className="h-8 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 text-xs font-semibold"
                  data-testid="feature-flag-confirm-ok"
                >
                  Yes, enable it
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPendingConfirm(null)}
                  disabled={busy === pendingConfirm.key}
                  className="h-8 rounded-full px-4 text-xs font-semibold"
                  data-testid="feature-flag-confirm-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CRM INTEGRATIONS PANEL (Tasks 2-5) ============
// One card, 4 provider rows. Each row: label, status pill, connect/disconnect
// flow. Manual token entry only — no OAuth UI. Tokens are never echoed back
// after save; the backend returns a masked preview + connection state.
const CRM_PROVIDERS = [
  {
    key: "hubspot",
    label: "HubSpot",
    description: "Private App Token",
    fields: [
      { key: "token", label: "Access token", type: "password", placeholder: "pat-na1-xxxxxxxx..." },
    ],
    tokenHelp: "Create a Private App in HubSpot → copy the access token.",
  },
  {
    key: "salesforce",
    label: "Salesforce",
    description: "REST API token + instance URL",
    fields: [
      { key: "token", label: "Access token", type: "password", placeholder: "00D...!AQ..." },
      {
        key: "instance_url", label: "Instance URL", type: "text",
        placeholder: "https://yourorg.my.salesforce.com",
      },
    ],
    tokenHelp: "Use a Connected App's access token + your instance URL.",
  },
  {
    key: "dynamics",
    label: "Microsoft Dynamics 365",
    description: "Web API token + base URL",
    fields: [
      { key: "token", label: "Access token", type: "password", placeholder: "ey..." },
      {
        key: "base_url", label: "Base URL", type: "text",
        placeholder: "https://yourorg.crm.dynamics.com/api/data/v9.2/",
      },
    ],
    tokenHelp: "Paste your Microsoft Graph / Dynamics Web API bearer token.",
  },
  {
    key: "zoho",
    label: "Zoho CRM",
    description: "OAuth access token",
    fields: [
      { key: "token", label: "Access token", type: "password", placeholder: "1000.xxxxx" },
    ],
    tokenHelp: "Generate an access token via Zoho's OAuth self-service flow.",
  },
];

function CrmConnectionsPanel() {
  const [connections, setConnections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // {provider: {token, instance_url?, base_url?}}
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await listCrmConnections();
      setConnections(r);
    } catch {
      // Never-throw backend; network only.
      setConnections({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (provider) => {
    const payload = editing[provider] || {};
    if (!(payload.token || "").trim()) {
      toast.error("Access token is required.");
      return;
    }
    setBusy(provider);
    try {
      const r = await connectCrm(provider, payload);
      setConnections(r);
      setEditing((prev) => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });
      toast.success(`${provider[0].toUpperCase()}${provider.slice(1)} connected`, {
        description: "Activities from emails and calls will now sync automatically.",
      });
    } catch (err) {
      toast.error("Connection failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}?`)) return;
    setBusy(provider);
    try {
      const r = await disconnectCrm(provider);
      setConnections(r);
      toast.success(`${provider} disconnected`);
    } catch (err) {
      toast.error("Disconnect failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="crm-connections-panel"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
          <Plug className="h-4 w-4 text-[#002FA7]" />
        </div>
        <div>
          <div className="label-caps text-[#002FA7]">CRM Integrations</div>
          <div className="text-[12px] text-slate-600">
            Push contacts + log activities on every email and call.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-slate-50 animate-pulse rounded-md" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {CRM_PROVIDERS.map((p) => {
            const state = connections?.[p.key] || { connected: false };
            const isEditing = !!editing[p.key];
            return (
              <div
                key={p.key}
                className="rounded-lg border border-slate-200 bg-slate-50/40 p-4"
                data-testid={`crm-row-${p.key}`}
                data-connected={state.connected ? "true" : "false"}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {p.label}
                      {state.connected ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-full"
                          data-testid={`crm-status-${p.key}`}
                        >
                          <CheckCircle2 className="h-2.5 w-2.5" /> Connected
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full"
                          data-testid={`crm-status-${p.key}`}
                        >
                          Not connected
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      {p.description}
                      {state.connected && state.token_masked && (
                        <span className="ml-2 font-mono text-slate-600">
                          {state.token_masked}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {state.connected && !isEditing && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDisconnect(p.key)}
                        disabled={busy === p.key}
                        className="h-8 border-slate-200 text-slate-600"
                        data-testid={`crm-disconnect-${p.key}`}
                      >
                        <Unlink className="h-3.5 w-3.5 mr-1" />
                        Disconnect
                      </Button>
                    )}
                    {!isEditing && (
                      <Button
                        size="sm"
                        onClick={() =>
                          setEditing((prev) => ({
                            ...prev,
                            [p.key]: {
                              token: "",
                              instance_url: state.instance_url || "",
                              base_url: state.base_url || "",
                            },
                          }))
                        }
                        className="h-8 bg-[#002FA7] hover:bg-[#002072]"
                        data-testid={`crm-connect-${p.key}`}
                      >
                        <Link2 className="h-3.5 w-3.5 mr-1" />
                        {state.connected ? "Update" : `Connect ${p.label}`}
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div
                    className="mt-3 pt-3 border-t border-slate-100 space-y-2"
                    data-testid={`crm-form-${p.key}`}
                  >
                    {p.fields.map((f) => (
                      <div key={f.key}>
                        <Label className="label-caps !text-[10px] !tracking-[0.2em] text-slate-500">
                          {f.label}
                        </Label>
                        <Input
                          type={f.type}
                          placeholder={f.placeholder}
                          value={editing[p.key]?.[f.key] || ""}
                          onChange={(e) =>
                            setEditing((prev) => ({
                              ...prev,
                              [p.key]: {
                                ...(prev[p.key] || {}),
                                [f.key]: e.target.value,
                              },
                            }))
                          }
                          data-testid={`crm-input-${p.key}-${f.key}`}
                          className="h-9 mt-1"
                        />
                      </div>
                    ))}
                    <div className="text-[11px] text-slate-500">{p.tokenHelp}</div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => handleSave(p.key)}
                        disabled={busy === p.key}
                        className="h-8 bg-[#002FA7] hover:bg-[#002072]"
                        data-testid={`crm-save-${p.key}`}
                      >
                        {busy === p.key ? "Saving…" : "Save & connect"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setEditing((prev) => {
                            const next = { ...prev };
                            delete next[p.key];
                            return next;
                          })
                        }
                        className="h-8 border-slate-200"
                        data-testid={`crm-cancel-${p.key}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

